/**
 * PageLayout - Consistent page wrapper for all dashboard pages
 *
 * Provides:
 * - Consistent spacing (space-y-6)
 * - PageHeader integration
 * - Automatic layout structure
 *
 * Usage:
 * <PageLayout
 *   title="Page Title"
 *   description="Page description"
 *   icon={<Icon />}
 *   actions={<Buttons />}
 *   onRefresh={handleRefresh}
 *   isRefreshing={loading}
 * >
 *   <YourPageContent />
 * </PageLayout>
 */

import { ReactNode } from 'react'
import { PageHeader } from '@/components/layout/page-header'

export interface PageLayoutProps {
  children: ReactNode

  // Page header props
  title: string
  description?: string
  icon?: ReactNode
  actions?: ReactNode
  onRefresh?: () => void
  isRefreshing?: boolean
}

export function PageLayout({
  children,
  title,
  description,
  icon,
  actions,
  onRefresh,
  isRefreshing = false
}: PageLayoutProps) {
  return (
    <div className="space-y-6">
      {/* Page Header - consistent across all pages */}
      <PageHeader
        title={title}
        description={description}
        icon={icon}
        action={actions}
        onRefresh={onRefresh}
        isLoading={isRefreshing}
      />

      {/* Page Content */}
      {children}
    </div>
  )
}
